import { BasicInput, PagedResponse } from '@elotech/components';
import { AxiosPromise } from 'axios';
import { UNCPessoa } from 'common/type';
import { FieldProps, FormikProps, getIn } from 'formik';
import { FC, useCallback, useState } from 'react';

import { SILENT_REJECTION } from '../../error/ErrorBoundary';
import { AddPessoaModal } from './AddPessoaModal';
import { PessoaSearch, PessoaSearchProps } from './PessoaSearch';

type PessoaServiceTemplate = {
  save: (pessoa: UNCPessoa) => AxiosPromise<UNCPessoa>;
  findPessoaByDocumento: (documento: string) => AxiosPromise<UNCPessoa>;
};

type FormikPessoaSearchProps = Partial<PessoaSearchProps> & {
  name: string;
  size?: number;
  fast?: boolean;
  serviceModalCreate?: PessoaServiceTemplate;
  onSearch: (search: string) => AxiosPromise<PagedResponse<UNCPessoa>>;
} & (
    | {
        multiple: true;
        onItemSelected?: (
          form: FormikProps<any>,
          name: string,
          value: UNCPessoa[]
        ) => void;
      }
    | {
        multiple?: false | undefined;
        onItemSelected?: (
          form: FormikProps<any>,
          name: string,
          value: UNCPessoa
        ) => void;
      }
  );

export const FormikPessoaSearch: FC<FormikPessoaSearchProps> = ({
  name,
  size = 4,
  fast = false,
  serviceModalCreate,
  onSelect,
  onItemSelected,
  ...props
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectPromise, setSelectPromise] = useState<{
    resolve: (value: any) => void;
    reject: (value: any) => void;
  }>();

  const closeModal = useCallback(
    (data?: UNCPessoa) => {
      setOpenModal(false);
      if (data?.id) {
        return selectPromise?.resolve({ data });
      }
      selectPromise?.reject(SILENT_REJECTION);
    },
    [setOpenModal, selectPromise]
  );

  const renderPessoaSearch = ({
    field: { onChange, ...fieldProps },
    form
  }: FieldProps) => {
    return (
      <>
        <PessoaSearch
          {...props}
          {...fieldProps}
          onSelect={(name, value) => {
            if (onSelect) {
              onSelect(name, value);
            } else {
              form.setFieldValue(name, value);
              form.setFieldTouched(name, true);
              if (onItemSelected) {
                onItemSelected(form, name, value);
              }
            }
          }}
          onCreateValue={
            serviceModalCreate
              ? () =>
                  new Promise((resolve, reject) => {
                    setOpenModal(true);
                    setSelectPromise({ resolve, reject });
                  })
              : undefined
          }
          error={
            !!(
              getIn(form.errors, name) &&
              (getIn(form.touched, name) || form.submitCount > 0)
            )
          }
        />

        {openModal && serviceModalCreate && (
          <AddPessoaModal
            onAdd={pessoa => {
              return serviceModalCreate.save(pessoa).then(({ data }) => {
                form.setFieldValue(name, data);
                form.setFieldTouched(name, true);
                return data;
              });
            }}
            findPessoa={serviceModalCreate.findPessoaByDocumento}
            onClose={closeModal}
          />
        )}
      </>
    );
  };

  return (
    <BasicInput
      noLabel
      {...props}
      name={name}
      fast={fast}
      size={size}
      render={renderPessoaSearch}
    />
  );
};
